import React, { useEffect, useState } from "react";
import styles from "./css/UpdateProjectPage.module.css";
import { useTranslation } from "react-i18next";
import { CreateProjectData, Project } from "@/types/project";
import { Button } from "@/components/Buttons";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { BackButton } from "@/components/Buttons/BackButton";
import { FilesTypes, UploadFilesTableData } from "@/types/file";
import { currentDate } from "@/utils/date";
import { CreateProject } from "@/components/pageParts/project/CreateProject";
import { useFetchAllEntities, useFetchAllProjects, useFetchCity } from "@/utils/fetchData";
import { getEntityIdPair } from "@/utils/getEntityPairId";
import { useDeleteProject } from "@/hooks/project/useDeleteProject";
import { setToast } from "@/store/systemSlice";
import { DeleteIcon } from "@/icons/DeleteIcon";
import { useUpdateProject } from "@/hooks/project/useUpdateProject";
import { useGetProject } from "@/hooks/project/useGetProject";
import { LoadingPageAnimation } from "@/components/addons/LoadingPageAnimation";

export const UpdateProjectPage = () => {
    const navigate = useNavigate();
    const {t} = useTranslation("CreateProjectPage");
    const {entityName, cityId, projectId} = useParams<{entityName: string, cityId: string, projectId: string}>();
    const project = useSelector((state: RootState) => state.projects.projects.find((project) => project.id === projectId));
    const city = useSelector((state: RootState) => state.entity.cities.find((city) => city.id === cityId));
    const {userAo, userCity, entityType} = useSelector((state: RootState) => state.entity);
    const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([false, true, true, true, true]);
    const [projectFieldsCompleted, setProjectFieldsCompleted] = useState<boolean>(false);
    const dispatch = useDispatch();
    const {getAllCities} = useFetchAllEntities();
    const fetchCity = useFetchCity();

    const updateCity = () => {
        if(entityType === "ao") {
            getAllCities.mutate({ao_id: userAo?.id as string});
            navigate(`/${entityName}/projects/${cityId}`);
        }
        else {
            fetchCity.mutate({city_id: userCity?.id as string});
            navigate(`/${entityName}/projects`);
        }
    }

    const deleteProject = useDeleteProject({
        onSuccess(data) {
            dispatch(
                setToast({
                    toastTitle: data.message,
                    toastMessage: "",
                    toastType: "success",   
                })
            );
            setDeleteLoading(false);
            updateCity();
        },
        onError(error) {
            dispatch(
                setToast({
                    toastTitle: error.response.data.message,    
                    toastMessage: "",
                    toastType: "error",
                })
            );
            setDeleteLoading(false);
        }
    });
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [projectData, setProjectData] = useState<CreateProjectData>();
    const projects = useSelector((state: RootState) => state.projects.projects);
    const [isProjectNameExists, setIsProjectNameExists] = useState<boolean>(false);


    const convertCreateProjectDataToUpdateProjectData = (data: CreateProjectData) => {

        const {...rest} = data;
        let updateData = {
            ...rest,
            project_id: projectId as string
        }
        let files = projectFiles.filter(file => file.file).reduce((acc, file) => ({
            ...acc,
            [file.type]: file.file
        }), {} as Record<FilesTypes, File>);
        if(files) {
            updateData.files = files;
        }
        return updateData;
    }

    const convertProjectDataToCreateProjectData = (createData: Project) => {
        const {created_at, created_by, id, buried_electric_infrastructure, ...rest} = createData;
        return {
            ...rest,
            files: {}
        }
    }

    const getProject = useGetProject({
        onSuccess(data) {
            const projData = convertProjectDataToCreateProjectData(data as Project);
            setProjectData(projData);
            setProjectFiles(prev => prev.map(fileData => {
                const file = data.files[fileData.type]
                if (file) {
                    return {
                        ...fileData,
                        file_name: file.file_name,
                        created_at: file.created_at || currentDate(),
                        file: file.url as unknown as File
                    } as UploadFilesTableData;
                }
                return fileData;
            }));
        },
        onError(error) {
            navigate("/404");
        }
    })

    useEffect(() => {
        if(!project) {
            getProject.mutate({city_id: cityId as string, project_id: projectId as string, ...getEntityIdPair(entityType, userCity, userAo)});
        }
        else {
            const projData = convertProjectDataToCreateProjectData(project as Project);
            setProjectData(projData);
            
            if (project?.files) {
                setProjectFiles(prev => prev.map(fileData => {
                    const file = project.files![fileData.type]
                    if (file) {
                        return {
                            ...fileData,
                            file_name: file.file_name,
                            created_at: file.created_at || currentDate(),
                            file: file.url as unknown as File
                        } as UploadFilesTableData;
                    }
                    return fileData;
                }));
            }
        }
    }, [cityId, projectId, project]);
    

    const updateProject = useUpdateProject({
        onSuccess(data) {
            dispatch(
                setToast({
                    toastTitle: data.message,
                    toastMessage: "",
                    toastType: "success",
                })
              );
            setUpdateLoading(false);
            updateCity();
            if(entityType === "ao") {
                navigate(`/${entityName}/projects/${cityId}`);
            }
            else {
                navigate(`/${entityName}/projects`);
            }
        },
        onError(error) {
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
              );
            setUpdateLoading(false);
        }
    })

    const [projectFiles, setProjectFiles] = useState<UploadFilesTableData[]>([
        {
            type: "blue line",
            file: null,
            file_name: "",
            created_at: "",
            accept_types: [".geojson", ".dxf", ".shp"]
        }
    ]);

    useEffect(() => {
        if (!city && !userCity) {
            navigate("/404");
        }
    }, [city]);

    const handleUpdateProjectData = (data: CreateProjectData) => {
        // Check if the name exists in other projects (excluding current project)
        const nameExists = projects.some(project => 
            project.name === data.name && project.id !== projectId
        );
        setIsProjectNameExists(nameExists);
        setProjectData(data);
        
        // Update steps completion based on name existence
        setStepsCompleted(prev => {
            const newSteps = [...prev];
            newSteps[0] = Boolean(data.name) && !nameExists && projectFieldsCompleted;
            return newSteps;
        });
    }

    const handleUploadFile = (type: FilesTypes, file: File, project: boolean) => {
        const newProjectFiles = [...projectFiles];
        const index = newProjectFiles.findIndex(file => file.type === type);
        newProjectFiles[index] = {type, file, file_name: file.name, created_at: currentDate(), accept_types: projectFiles[index].accept_types};
        setProjectFiles(newProjectFiles);
    }

    const handleUpdateProject = () => {
        setUpdateLoading(true);
        updateProject.mutate(convertCreateProjectDataToUpdateProjectData(projectData as CreateProjectData));
    }

    const handleDeleteProject = () => {
        setDeleteLoading(true);
        deleteProject.mutate({city_id: cityId as string, project_id: projectId as string, ...getEntityIdPair(entityType, userCity, userAo)});
    }

    return (
        <>
        {projectData ? 
        <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1 className={styles.title}>{"עריכת פרוייקט"}</h1>
                    <div className={styles.backButtonContainer}>
                            <Button icon="left-side" text="מחיקה" id="delete-btn" onClick={handleDeleteProject} iconComponent={DeleteIcon} color="red" ready={true} className={styles.deleteBtn} loading={deleteLoading} disabled={updateLoading || deleteLoading}/>
                            <BackButton/>
                    </div>
                </div>
                <div className={styles.formContainer}>
                    <CreateProject onChange={handleUpdateProjectData} data={projectData} setStepsCompleted={setStepsCompleted} projectFiles={projectFiles} onUploadFile={(type: FilesTypes, file: File) => handleUploadFile(type, file, true)} fieldsCompleted={projectFieldsCompleted} setFieldsCompleted={setProjectFieldsCompleted} isProjectNameExists={isProjectNameExists}/>
                    <div className={styles.stepsContainerOnePage}>
                        <div className={styles.stepsSubContainer}>
                            <Button icon="no" text={`ערוך פרוייקט`} onClick={handleUpdateProject} id="update-btn" className={styles.continueBtn} disabled={!stepsCompleted[0] || updateLoading } loading={updateLoading}/>
                        </div>
                    </div>
                </div>
        </div>: <LoadingPageAnimation/>}
        </>
    );
}